import { validateMapWorkspaceNotice } from "./FmMapWorkspaceNotice.validation";
import {
  validateMapWorkspaceOrientationShape,
} from "./FmMapWorkspaceOrientation.validation";
import {
  MAP_WORKSPACE_STATES,
  type FmMapWorkspaceDescriptor,
} from "./FmMapWorkspaceRoot.types";
import {
  assertMapWorkspaceExactObject as exactObject,
  requireMapWorkspaceEnum,
  requireMapWorkspaceFields as requireFields,
  requireMapWorkspaceText as requireText,
  validateMapWorkspaceAvailabilityShape as validateAvailabilityShape,
} from "./FmMapWorkspaceValidation.shared";
const MAP_DATA_FIELDS = [
  "activePropertyId",
  "propertyForestContext",
  "propertyForestContextLoading",
  "riskOverlay",
  "riskInventoryRevision",
  "restoredSelection",
  "restoredViewport",
  "visualizationData",
  "measurementPlotOverlay",
  "onViewportChange",
] as const;
const REQUIRED_MAP_DATA_FIELDS = MAP_DATA_FIELDS.filter(
  (field) => field !== "measurementPlotOverlay",
);

export function validateMapWorkspaceActionShape(
  action: {
    intent: string;
    tool?: unknown;
    dismissesPanel?: unknown;
    confirm?: {
      title: unknown;
      description?: unknown;
      confirmLabel?: unknown;
      cancelLabel?: unknown;
    };
  },
  path: string,
): void {
  exactObject(action, ["id", "label", "intent", "tool", "dismissesPanel", "disabledReason", "confirm"], path);
  requireMapWorkspaceEnum(
    action.intent,
    ["primary", "secondary", "inspect", "destructive"],
    `${path}.intent`,
  );
  if (action.tool !== undefined) {
    requireMapWorkspaceEnum(
      action.tool,
      ["create", "refresh", "search"],
      `${path}.tool`,
    );
  }
  if (
    action.dismissesPanel !== undefined
    && typeof action.dismissesPanel !== "boolean"
  ) {
    throw new Error(
      `Invalid map workspace descriptor: ${path}.dismissesPanel must be boolean.`,
    );
  }
  if (!action.confirm) return;
  exactObject(
    action.confirm,
    ["title", "description", "confirmLabel", "cancelLabel"],
    `${path}.confirm`,
  );
  requireFields(action.confirm, ["title"], `${path}.confirm`);
}

function validateRadarShape(descriptor: FmMapWorkspaceDescriptor): void {
  exactObject(
    descriptor.radar,
    [
      "propertyId",
      "window",
      "state",
      "frames",
      "affectedStandCount",
      "maximumIntensity",
      "accumulatedRain",
      "noDataReason",
      "error",
      "summaryError",
      "reload",
      "copy",
    ],
    "radar",
  );
  if (descriptor.radar.window) {
    exactObject(descriptor.radar.window, ["start", "end"], "radar.window");
    requireFields(descriptor.radar.window, ["start", "end"], "radar.window");
    if (descriptor.radar.window.start !== null) {
      requireText(descriptor.radar.window.start, "radar.window.start");
    }
    if (descriptor.radar.window.end !== null) {
      requireText(descriptor.radar.window.end, "radar.window.end");
    }
  }
  descriptor.radar.frames.forEach((frame, index) => {
    exactObject(
      frame,
      ["id", "capturedAt", "grid", "render"],
      `radar.frames[${index}]`,
    );
    exactObject(frame.grid, ["crs", "bbox"], `radar.frames[${index}].grid`);
    exactObject(
      frame.render,
      frame.render.status === "ready"
        ? ["status", "imageUrl"]
        : ["status", "reason"],
      `radar.frames[${index}].render`,
    );
    if (!["ready", "unavailable"].includes(frame.render.status)) {
      throw new Error(
        `Invalid map workspace descriptor: radar.frames[${index}].render.status is unsupported.`,
      );
    }
    requireFields(
      frame.render,
      frame.render.status === "ready"
        ? ["status", "imageUrl"]
        : ["status", "reason"],
      `radar.frames[${index}].render`,
    );
  });
  validateAvailabilityShape(
    descriptor.radar.affectedStandCount,
    "radar.affectedStandCount",
  );
  validateAvailabilityShape(descriptor.radar.maximumIntensity, "radar.maximumIntensity");
  validateAvailabilityShape(descriptor.radar.accumulatedRain, "radar.accumulatedRain");
  exactObject(
    descriptor.radar.copy,
    [
      "title",
      "ariaLabel",
      "controlsAriaLabel",
      "reloadAria",
      "previousAria",
      "playAria",
      "pauseAria",
      "nextAria",
      "opacityLabel",
      "timeRangeLabel",
      "selectProperty",
      "loadingTimeline",
      "unavailable",
      "noFrames",
      "frameUnavailable",
      "affectedStands",
      "affectedSummary",
      "affectedSummaryWithRain",
      "noStandOverlap",
      "loaded",
      "noTimestamp",
    ],
    "radar.copy",
  );
  if (!MAP_WORKSPACE_STATES.includes(descriptor.radar.state)) {
    throw new Error(
      "Invalid map workspace descriptor: radar.state is unsupported.",
    );
  }
}

function validateRootShape(descriptor: FmMapWorkspaceDescriptor): void {
  exactObject(
    descriptor,
    [
      "id",
      "propertyId",
      "mode",
      "modeLabel",
      "story",
      "mapSlotKey",
      "mapData",
      "viewport",
      "geometryRefs",
      "baseMaps",
      "activeBaseMapId",
      "layers",
      "radar",
      "features",
      "selection",
      "inspection",
      "source",
      "filters",
      "actions",
      "panels",
      "dialogs",
      "activePanelId",
      "state",
      "copy",
      "orientation", "notice",
      "onEvent",
    ],
    "root",
  );
  exactObject(descriptor.viewport, ["id", "revision", "frame"], "viewport");
  if (descriptor.mapData !== null) {
    exactObject(descriptor.mapData, MAP_DATA_FIELDS, "mapData");
    requireFields(descriptor.mapData, REQUIRED_MAP_DATA_FIELDS, "mapData");
  }
  requireMapWorkspaceEnum(
    descriptor.mode,
    ["find", "orient", "inspect", "risk", "refine"],
    "mode",
  );
  requireMapWorkspaceEnum(
    descriptor.viewport.frame,
    ["all", "property", "selection", "risk"],
    "viewport.frame",
  );
  validateRadarShape(descriptor);
  exactObject(
    descriptor.story,
    [
      "ownerQuestion",
      "literalAnswer",
      "dominantBeat",
      "requiredServedFields",
      "requiredProvenance",
      "allowedTransitions",
      "stateBehavior",
      "mobileOrder",
      "conversionRole",
    ],
    "story",
  );
  requireMapWorkspaceEnum(descriptor.story.dominantBeat, ["map"], "story.dominantBeat");
  requireMapWorkspaceEnum(
    descriptor.story.conversionRole,
    ["find", "orient", "inspect", "assess-risk", "refine"],
    "story.conversionRole",
  );
  exactObject(
    descriptor.story.stateBehavior,
    MAP_WORKSPACE_STATES,
    "story.stateBehavior",
  );
  MAP_WORKSPACE_STATES.forEach((state) =>
    exactObject(
      descriptor.story.stateBehavior[state],
      ["answer", "retainsServedAnswer"],
      `story.stateBehavior.${state}`,
    ),
  );
  exactObject(descriptor.source, ["id", "label"], "source");
  exactObject(descriptor.state, ["kind", "label", "recoveryActionId"], "state");
  requireMapWorkspaceEnum(descriptor.state.kind, MAP_WORKSPACE_STATES, "state.kind");
  exactObject(
    descriptor.copy,
    ["rendererUnavailable", "appliedFiltersAriaLabel"],
    "copy",
  );
  const selectionFields =
    descriptor.selection.kind === "none"
      ? ["kind"]
      : descriptor.selection.kind === "property"
        ? ["kind", "id", "label", "geometryRef", "propertyId"]
        : descriptor.selection.kind === "stand"
          ? ["kind", "id", "label", "geometryRef", "propertyId", "standId"]
          : descriptor.selection.kind === "campaign"
            ? [
                "kind",
                "id",
                "label",
                "geometryRef",
                "campaignId",
                "campaignIds",
                "propertyIds",
                "plotIds",
              ]
            : descriptor.selection.kind === "plot"
              ? [
                  "kind",
                  "id",
                  "label",
                  "geometryRef",
                  "propertyId",
                  "campaignId",
                  "plotId",
                  "standId",
                ]
              : [
              "kind",
              "id",
              "label",
              "geometryRef",
              "propertyId",
              "riskId",
              "severity",
            ];
  exactObject(descriptor.selection, selectionFields, "selection");
  if (
    !["none", "property", "stand", "campaign", "plot", "risk"].includes(
      descriptor.selection.kind,
    )
  ) {
    throw new Error(
      "Invalid map workspace descriptor: selection.kind is unsupported.",
    );
  }
  requireFields(descriptor.selection, selectionFields, "selection");
  if (descriptor.selection.kind === "risk") {
    requireMapWorkspaceEnum(
      descriptor.selection.severity,
      ["elevated", "high", "severe"],
      "selection.severity",
    );
  }
}

function validateBaseMapShapes(descriptor: FmMapWorkspaceDescriptor): void {
  descriptor.baseMaps.forEach((baseMap, index) => {
    exactObject(baseMap, ["id", "label"], `baseMaps[${index}]`);
    requireMapWorkspaceEnum(
      baseMap.id,
      ["streets", "satellite", "terrain"],
      `baseMaps[${index}].id`,
    );
  });
}

function validateLayerShapes(descriptor: FmMapWorkspaceDescriptor): void {
  descriptor.layers.forEach((layer, index) => {
    exactObject(
      layer,
      layer.kind === "risk"
        ? ["id", "sourceId", "label", "kind", "visible", "access"]
        : ["id", "sourceId", "label", "kind", "visible"],
      `layers[${index}]`,
    );
    if (layer.kind === "risk") {
      if (
        !["available", "loading", "error", "unavailable", "not-evaluable", "locked"].includes(
          layer.access.kind,
        )
      ) {
        throw new Error(
          `Invalid map workspace descriptor: layers[${index}].access.kind is unsupported.`,
        );
      }
      const accessFields =
        layer.access.kind === "available"
          ? ["kind", "advisory"]
          : layer.access.kind === "loading"
            ? ["kind", "label"]
            : layer.access.kind === "error"
              ? ["kind", "message"]
              : layer.access.kind === "unavailable"
                ? ["kind", "reason"]
                : layer.access.kind === "not-evaluable"
                  ? ["kind", "reason", "unwatchedStandCount"]
                  : ["kind", "title", "explanation", "actionId", "actionLabel"];
      exactObject(layer.access, accessFields, `layers[${index}].access`);
      requireFields(layer.access, accessFields, `layers[${index}].access`);
    }
  });
}

function validateFeatureShapes(descriptor: FmMapWorkspaceDescriptor): void {
  descriptor.features.forEach((feature, index) => {
    exactObject(
      feature,
      [
        "id",
        "sourceId",
        "geometryRef",
        "label",
        "kind",
        "propertyId",
        "campaignId",
        "plotId",
        "standId",
        "relatedPlotIds",
        "relatedCampaignIds",
        "relatedPropertyIds",
        "mappingStatus",
        "description",
      ],
      `features[${index}]`,
    );
    requireMapWorkspaceEnum(
      feature.kind,
      ["property", "stand", "campaign", "plot", "risk"],
      `features[${index}].kind`,
    );
    const identityFields = feature.kind === "property"
      ? ["propertyId"]
      : feature.kind === "stand"
        ? ["propertyId", "standId"]
        : feature.kind === "campaign"
          ? ["campaignId"]
          : feature.kind === "plot"
            ? ["propertyId", "campaignId", "plotId"]
            : ["propertyId"];
    requireFields(
      feature,
      identityFields,
      `features[${index}]`,
    );
    if (feature.kind === "property" || feature.kind === "risk") {
      requireText(feature.propertyId, `features[${index}].propertyId`);
    } else if (feature.kind === "stand") {
      requireText(feature.propertyId, `features[${index}].propertyId`);
      requireText(feature.standId, `features[${index}].standId`);
    } else if (feature.kind === "campaign") {
      requireText(feature.campaignId, `features[${index}].campaignId`);
    } else if (feature.kind === "plot") {
      requireText(feature.propertyId, `features[${index}].propertyId`);
      requireText(feature.campaignId, `features[${index}].campaignId`);
      requireText(feature.plotId, `features[${index}].plotId`);
    }
  });
}

function validateFilterShapes(descriptor: FmMapWorkspaceDescriptor): void {
  descriptor.filters.forEach((filter, filterIndex) => {
    exactObject(
      filter,
      ["id", "sourceId", "label", "operator", "options", "selectedValues"],
      `filters[${filterIndex}]`,
    );
    requireMapWorkspaceEnum(
      filter.operator,
      ["one-of", "boolean"],
      `filters[${filterIndex}].operator`,
    );
    filter.options.forEach((option, optionIndex) => {
      exactObject(
        option,
        ["value", "label"],
        `filters[${filterIndex}].options[${optionIndex}]`,
      );
    });
  });
}

function validateDialogShapes(descriptor: FmMapWorkspaceDescriptor): void {
  (descriptor.dialogs ?? []).forEach((dialog, index) => {
    exactObject(
      dialog,
      dialog.kind === "form-flow"
        ? [
            "kind", "id", "open", "title", "closeLabel", "closeConfirmation", "size", "flow",
          ]
        : ["kind", "id", "open", "title", "closeLabel", "size", "workspace"],
      `dialogs[${index}]`,
    );
    requireMapWorkspaceEnum(
      dialog.kind,
      ["form-flow", "analysis-detail", "operational-detail"],
      `dialogs[${index}].kind`,
    );
    requireMapWorkspaceEnum(
      dialog.size,
      ["md", "lg"],
      `dialogs[${index}].size`,
    );
    if (dialog.kind === "analysis-detail") {
      requireMapWorkspaceEnum(dialog.size, ["lg"], `dialogs[${index}].size`);
    }
    if (dialog.kind === "form-flow") {
      requireMapWorkspaceEnum(
        dialog.size,
        ["md", "lg"],
        `dialogs[${index}].size`,
      );
      if (dialog.closeConfirmation) {
        exactObject(
          dialog.closeConfirmation,
          ["title", "description", "confirmLabel", "cancelLabel"],
          `dialogs[${index}].closeConfirmation`,
        );
      }
    }
    if (dialog.kind === "operational-detail") {
      requireMapWorkspaceEnum(dialog.size, ["lg"], `dialogs[${index}].size`);
    }
  });
}

function validateCollectionShapes(descriptor: FmMapWorkspaceDescriptor): void {
  validateBaseMapShapes(descriptor);
  validateLayerShapes(descriptor);
  validateFeatureShapes(descriptor);
  validateFilterShapes(descriptor);
  descriptor.actions.forEach((action, index) => {
    validateMapWorkspaceActionShape(action, `actions[${index}]`);
  });
  validateDialogShapes(descriptor);
}

export function validateMapWorkspaceShape(
  descriptor: FmMapWorkspaceDescriptor,
): void {
  validateRootShape(descriptor);
  validateCollectionShapes(descriptor);
  validateMapWorkspaceOrientationShape(descriptor);
  validateMapWorkspaceNotice(descriptor);
}
